/**
 * Client-side analytics-consent state (ePrivacy / GDPR). We store a single
 * explicit choice — "granted" or "denied" — that gates ONLY the PERSISTENT
 * analytics tier (on-device cookies/localStorage for cross-session identity +
 * session replay). Essential storage (auth session, the consent choice itself,
 * first-party UI preferences) never needs consent.
 *
 * Two-tier model: an anonymous, cookie-free basic reach measurement runs for
 * everyone WITHOUT consent (nothing is written to or read from the device, so
 * §25 TTDSG is not triggered — Art. 6(1)(f) GDPR). "granted" upgrades to the
 * persistent tier; "denied" opts analytics out entirely. See PostHogProvider.
 */
export type Consent = "granted" | "denied";

const KEY = "cm.consent.v1";
const EVENT = "cm-consent";

export function getConsent(): Consent | null {
  try {
    const v = localStorage.getItem(KEY);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

/**
 * Synchronous read of the stored decision at MODULE LOAD (before React mounts),
 * so PostHogProvider can init with the right persistence tier on the very first
 * paint without a re-init. SSR-safe: returns null when there is no window.
 */
export function readConsentSync(): Consent | null {
  if (typeof window === "undefined") return null;
  return getConsent();
}

export function setConsent(v: Consent): void {
  try {
    localStorage.setItem(KEY, v);
  } catch {
    /* private mode — treat as session-only */
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: v }));
  } catch {
    /* SSR / no window */
  }
}

/** Re-open the choice (footer "Cookie settings" / cookie policy page). */
export function resetConsent(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new CustomEvent(EVENT, { detail: null }));
  } catch {
    /* ignore */
  }
}

export function onConsentChange(fn: (v: Consent | null) => void): () => void {
  const handler = (e: Event) => fn((e as CustomEvent).detail as Consent | null);
  window.addEventListener(EVENT, handler);
  return () => window.removeEventListener(EVENT, handler);
}
